"use client";

import { CalendarSync, Key, Leaf, PackageCheck } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Benefits() {
  const header = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const grid = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const quote = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.3
  });
  const benefits = [{
    icon: Leaf,
    title: "Operational Efficiency",
    description: "Streamline your core business processes with intelligent automation that eliminates manual bottlenecks."
  }, {
    icon: PackageCheck,
    title: "Scalable Workflows",
    description: "Deploy robust systems that grow with your demand, ensuring consistent performance at any volume."
  }, {
    icon: CalendarSync,
    title: "Automated Scheduling",
    description: "Eliminate back-and-forth emails with smart booking systems that sync directly to your calendar."
  }, {
    icon: Key,
    title: "Data-Driven Growth",
    description: "Unlock actionable insights through integrated analytics and automated reporting tools."
  }];
  return <section data-section-id="447" id="industry-benefits" className="bg-background text-foreground py-24 px-6 sm:px-8 lg:px-12">
      <div className="mx-auto max-w-7xl">
        {}
        <div ref={header.ref} className={`mx-auto max-w-2xl text-center transition-all duration-700 ease-out ${header.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <span className="inline-block text-xs tracking-[0.25em] uppercase font-mono text-foreground/70">
            — Why AutolexaAi
          </span>
          <h2 className="font-default text-4xl sm:text-5xl leading-[1.05] mt-6 text-foreground">
            Architecting <span className="text-foreground/70">Autonomous</span> Business Systems.
          </h2>
          <p className="mt-5 text-muted-foreground text-base sm:text-lg max-w-xl mx-auto">
            We transform complex operations into seamless digital workflows, saving you time and driving measurable efficiency.
          </p>
        </div>

        {}
        <div ref={grid.ref} className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {benefits.map((benefit, i) => {
          const Icon = benefit.icon;
          return <div key={i} data-index={i} className={`group relative rounded-lg bg-card border border-border p-8 transition-all duration-500 ease-out hover:-translate-y-1 hover:border-foreground/30 hover:shadow-lg ${grid.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{
            transitionDelay: grid.isVisible ? `${i * 100}ms` : "0ms"
          }}>
                {}
                <div className="flex items-center justify-start">
                  <div className="flex items-center justify-center w-14 h-14 rounded-lg transition-colors duration-300 bg-accent">
                    <Icon className="w-7 h-7 transition-colors duration-300 group-hover:text-foreground/70" strokeWidth={1.25} style={{
                  color: "var(--foreground)"
                }} />
                  </div>
                </div>

                {}
                <h3 className="mt-8 font-default text-xl sm:text-2xl leading-snug text-foreground">
                  {benefit.title}
                </h3>

                {}
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                  {benefit.description}
                </p>

                {}
                <div className="mt-8 flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-lg bg-chart-2" />
                  <span className="h-px w-8 bg-chart-2 opacity-50" />
                </div>
              </div>;
        })}
        </div>

        {}
        <div ref={quote.ref} className={`mt-20 border-t border-b border-border py-10 sm:py-12 transition-all duration-700 ease-out ${quote.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="flex flex-col items-center text-center gap-4 max-w-3xl mx-auto">
            <p className="font-default italic text-2xl sm:text-3xl lg:text-4xl leading-snug text-foreground">
              &ldquo;AutolexaAi turned our chaotic manual processes into a perfectly synchronized machine.&rdquo;
            </p>
            <span className="text-xs tracking-[0.25em] uppercase font-mono mt-2 text-foreground/70">
              — Operations Director, Client
            </span>
          </div>
        </div>
      </div>
    </section>;
}